import React from 'react';
import clsx from 'clsx';
import { Select as AntSelect } from 'antd';
import { objectHasOwn } from 'src/utils/objectHasOwn';
import { Field } from '../../AlertCriteria/types/Field';
import styles from '../Components.module.less';

const ALL_VALUE = '-1';

type SelectValue = string | string[];

type Props<T extends SelectValue> = {
  label: string;
  placeholder: string;
  options: Field.Option[] | Field.OptGroup[];
  value?: T;
  error?: boolean;
  loading?: boolean;
  filterable?: boolean;
  onChange: (value: T) => void;
};

type SelectProps = Props<string>;
type MultiSelectProps = Props<string[]> & {
  maxTagCount?: number;
};

const filterConfig = {
  showSearch: true,
  optionFilterProp: 'children',
};

export const isOptGroup = (v: Field.Option | Field.OptGroup): v is Field.OptGroup => {
  const k: keyof Field.OptGroup = 'options';
  return objectHasOwn(v, k);
};

const renderOption = ({ value, label, title }: Field.Option) => (
  <AntSelect.Option key={value} value={value} title={title ?? label}>
    {label}
  </AntSelect.Option>
);

const renderOptGroup = ({ label, options }: Field.OptGroup) => (
  <AntSelect.OptGroup key={label} label={label}>
    {options.map(renderOption)}
  </AntSelect.OptGroup>
);

const renderSelectOptions = (options: Field.Option[] | Field.OptGroup[]) =>
  options.map((opt) => (isOptGroup(opt) ? renderOptGroup(opt) : renderOption(opt)));

const Wrapper: React.FC<{ label: string }> = ({ label, children }) => (
  <div className={styles.root}>
    {label && <h6>{label}</h6>}
    {children}
  </div>
);

export const Select: React.FC<SelectProps> = ({
  label,
  placeholder,
  options,
  value,
  error,
  loading,
  filterable,
  onChange,
}) => (
  <Wrapper label={label}>
    <AntSelect
      className={clsx(styles.field, error && styles.error)}
      placeholder={placeholder}
      value={options.length > 0 ? value : undefined}
      loading={loading}
      dropdownMatchSelectWidth={false}
      onChange={onChange}
      {...(filterable && filterConfig)}
    >
      {renderSelectOptions(options)}
    </AntSelect>
  </Wrapper>
);

export const MultiSelect: React.FC<MultiSelectProps> = ({
  label,
  placeholder,
  options,
  value,
  error,
  loading,
  maxTagCount,
  filterable,
  onChange,
}) => {
  const handleChange = (newValue: string[]) => {
    const prev = value ?? [];
    const diff = newValue.filter((id) => !prev.includes(id));
    if (diff.includes(ALL_VALUE)) {
      onChange([ALL_VALUE]);
    } else {
      onChange(newValue.filter((id) => id !== ALL_VALUE));
    }
  };

  return (
    <Wrapper label={label}>
      <AntSelect
        mode="multiple"
        className={clsx(styles.field, error && styles.error)}
        placeholder={placeholder}
        value={options.length > 0 ? value : undefined}
        loading={loading}
        maxTagCount={maxTagCount}
        dropdownMatchSelectWidth={false}
        onChange={handleChange}
        {...(filterable && filterConfig)}
      >
        {renderSelectOptions(options)}
      </AntSelect>
    </Wrapper>
  );
};
